import type { FC } from "react";
import { memo } from "react";
import { LinkCell } from "pro-features/components/Workspace/TableCells/LinkCell";
import { TrafficShare } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import type { IDistributionItemTable } from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersTableCompare";
import type { IFilterItem } from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import {
    HeaderWithMarker,
    StyledCellCategory,
    StyledTable,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/StyledComponents";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { allTrackers } from "app/services/track/track";

interface IDistributionByCategoriesProps {
    i18n: any;
    categoriesDistribution: IDistributionItemTable;
    topCategories: IFilterItem[];
    domains: { color: string; name: string }[];
    getLink: (key: string, value: string) => string;
    onClick: (category: string) => void;
}

const DistributionByCategoriesInner: FC<IDistributionByCategoriesProps> = (props) => {
    const { i18n, categoriesDistribution, topCategories, domains, getLink, onClick } = props;

    const transformData = () => {
        const topSixCategories = topCategories?.slice(0, 6).map((item) => item.Name);
        const data = topSixCategories.map((cat) => {
            const categoryChild = cat.substring(cat.indexOf("/") + 1);
            const category = categoryChild ? categoryChild : cat;
            const row = {
                category: category.replace(/_/g, " "),
                categoryApi: cat.replace("/", "~"),
            };

            domains.forEach((domain) => {
                row[domain.name] = [categoriesDistribution[domain.name]?.find((c) => c.Name === cat)?.Value];
            });

            return row;
        });

        if (topCategories.length > 5) {
            const row = {
                category: "Others",
                categoryApi: null,
            };
            domains.forEach((domain) => {
                const othersSum = data.reduce((acc, val) => {
                    if (val[domain.name][0]) {
                        acc += val[domain.name][0];
                    }
                    return acc;
                }, 0);

                row[domain.name] = othersSum <= 0 ? null : 1 - othersSum;
            });

            return [...data, row];
        } else {
            return data;
        }
    };

    const createColumns = (domains, getLink) => {
        const columns: any[] = [
            {
                field: "category",
                displayName: i18n("monetization.advertisers.categorydistribution.column.category"),
                headerComponent: DefaultCellHeader,
                cellComponent: (props) => {
                    const { value, row } = props;
                    return value === "Others" ? (
                        <div>{value}</div>
                    ) : (
                        <StyledCellCategory>
                            <LinkCell
                                href={getLink("industry", row.categoryApi)}
                                value={value}
                                title={value}
                                onClick={() => {
                                    allTrackers.dispatchTrackEvent(
                                        "Filter Table",
                                        "click",
                                        `Table/category/${value || ""}`,
                                    );

                                    onClick(row.categoryApi);
                                }}
                            />
                        </StyledCellCategory>
                    );
                },
                visible: true,
                minWidth: 250,
            },
        ];
        domains.forEach((domain) => {
            const newColumn = {
                field: domain.name,
                displayName: domain.name,
                headerComponent: HeaderWithMarker(domain),
                cellComponent: TrafficShare,
                minWidth: 100,
                maxWidth: 300,
                visible: true,
                innerColor: domain.color,
            };
            columns.push(newColumn);
        });
        return columns;
    };

    return topCategories &&
        topCategories.length &&
        categoriesDistribution &&
        Object.keys(categoriesDistribution) &&
        domains ? (
        <StyledTable
            className="MiniFlexTable"
            data={transformData()}
            columns={createColumns(domains, getLink)}
        />
    ) : (
        <NoDataLandscape
            title="analysis.publishers.advertisers.empty.state.title"
            subtitle={null}
        />
    );
};

const propsAreEqual = (prevProps, nextProps) => {
    const { categoriesDistribution, topCategories } = prevProps;
    const { categoriesDistribution: nextCategoriesData, topCategories: nextTopCategories } = nextProps;
    return categoriesDistribution === nextCategoriesData && nextTopCategories === topCategories;
};

export const AdvertisersDistributionByCategories = memo(DistributionByCategoriesInner, propsAreEqual);
